import { User, UserProfile } from '../types';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  user: User;
}

interface LoginCredentials {
  username: string;
  password: string;
}

class AuthService {
  private baseURL = process.env.REACT_APP_AUTH_BASE_URL || '/api/auth';
  private currentUser: User | null = null;

  // Username/Password login with new token system
  async loginWithCredentials(credentials: LoginCredentials): Promise<{ success: boolean; error?: string; user?: User }> {
    try {
      const response = await fetch(`${this.baseURL}/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      if (response.ok) {
        const data: TokenResponse = await response.json();
        
        // Store tokens
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        localStorage.setItem('tokenType', data.token_type);
        localStorage.setItem('tokenExpiry', (Date.now() + (data.expires_in * 1000)).toString());
        
        // Store user info
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          this.currentUser = data.user;
        }
        
        return { success: true, user: data.user };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return { 
          success: false, 
          error: errorData.error || errorData.detail || errorData.message || 'Invalid credentials' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Network error. Please check your connection and try again.' 
      };
    }
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return false;
      }

      // Check if token is expired
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      if (tokenExpiry && Date.now() > parseInt(tokenExpiry)) {
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          return false;
        }
      }

      // Verify token is still valid
      const response = await fetch(`${this.baseURL}/profile/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        return true;
      } else if (response.status === 401) {
        // Token is invalid, try to refresh
        const refreshed = await this.refreshToken();
        return refreshed;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Auth check error:', error);
      return false;
    }
  }

  // Refresh access token using refresh token
  async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        this.logout();
        return false;
      }

      const response = await fetch(`${this.baseURL}/token/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('tokenExpiry', (Date.now() + (data.expires_in * 1000)).toString());
        return true;
      } else {
        this.logout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      return false;
    }
  }

  // Get current user info with caching
  async getCurrentUser(): Promise<User | null> {
    try {
      // Return cached user if available
      if (this.currentUser) {
        return this.currentUser;
      }

      // Try to get user from localStorage first
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        this.currentUser = JSON.parse(storedUser);
        return this.currentUser;
      }

      // Fetch user profile from API
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return null;
      }

      const response = await fetch(`${this.baseURL}/profile/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const profile: UserProfile = await response.json();
        this.currentUser = profile.user;
        localStorage.setItem('user', JSON.stringify(profile.user));
        return this.currentUser;
      } else if (response.status === 401) {
        // Try to refresh token and retry
        const refreshed = await this.refreshToken();
        if (refreshed) {
          return this.getCurrentUser();
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  // Get user profile with additional information
  async getUserProfile(): Promise<UserProfile | null> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return null;
      }

      const response = await fetch(`${this.baseURL}/profile/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        return await response.json();
      } else if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          return this.getUserProfile();
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  // Get user's active tokens
  async getUserTokens(): Promise<any[] | null> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return null;
      }

      const response = await fetch(`${this.baseURL}/tokens/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.tokens;
      } else if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          return this.getUserTokens();
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting user tokens:', error);
      return null;
    }
  }

  // Validate current token
  async validateToken(): Promise<boolean> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/token/validate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: accessToken
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.valid;
      }
      return false;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  // Revoke current token
  async revokeToken(): Promise<boolean> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/token/revoke/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: accessToken
        }),
      });

      return response.ok;
    } catch (error) {
      console.error('Token revocation error:', error);
      return false;
    }
  }

  // Get stored auth token
  getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  // Get authorization header
  getAuthHeader(): string | null {
    const token = this.getAuthToken();
    return token ? `Bearer ${token}` : null;
  }

  // Check if token is expired
  isTokenExpired(): boolean {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) {
      return true;
    }
    return Date.now() > parseInt(tokenExpiry);
  }

  // Get token expiry time
  getTokenExpiry(): Date | null {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    return tokenExpiry ? new Date(parseInt(tokenExpiry)) : null;
  }

  // Redirect to Microsoft login (legacy support)
  loginWithMicrosoft(): void {
    // IMPORTANT: SSO endpoint lives on the backend host, not the SPA host.
    // In dev, proxying may make relative URLs work, but in staging/prod the
    // frontend and backend are typically different domains.
    const explicit = process.env.REACT_APP_SSO_BASE_URL;
    const apiBase = process.env.REACT_APP_API_BASE_URL; // e.g. https://pulse-staging-api.qure.ai/api/v1
    const authBase = process.env.REACT_APP_AUTH_BASE_URL; // e.g. https://pulse-staging-api.qure.ai/api/auth

    const base =
      explicit ||
      (apiBase ? apiBase.replace(/\/api\/v1\/?$/, '') : undefined) ||
      (authBase ? authBase.replace(/\/api\/auth\/?$/, '') : undefined) ||
      '';

    const url = `${base}/sso/microsoft/`;
    window.location.href = url;
  }

  // Logout with token revocation
  async logout(): Promise<void> {
    try {
      // Try to revoke current token
      await this.revokeToken();
    } catch (error) {
      console.error('Error revoking token during logout:', error);
    } finally {
      // Always clear local storage and cached data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenType');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('user');
      sessionStorage.clear();
      this.currentUser = null;
      
      // Redirect to home page after logout
      window.location.href = '/';
    }
  }

  // Clear authentication state without API call
  clearAuth(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('user');
    sessionStorage.clear();
    this.currentUser = null;
  }
}

const authService = new AuthService();
export default authService; 